import type { Player } from '@infiltre/shared';
import { motion } from 'motion/react';
import { useT } from '@/shared/lib/i18n';
import { mugshotForPlayer } from '@/shared/lib/mugshot';
import { MugshotPortrait, PaperSheet, Pin } from '@/shared/ui';

export type VoteCandidateGridProps = {
  candidates: readonly Player[];
  /** Currently picked candidate (two-step flow). Omit for instant-vote. */
  pickedId?: string | null;
  /** Tapping a card triggers this. Omit (with `disabled`) for read-only display. */
  onPick?: (id: string) => void;
  disabled?: boolean;
};

const TILTS = [-3.5, 2.5, -2, 3, -3, 4, -4.5, 1.5, -1.5, 2];

const fileTag = (i: number): string => `F-${String(((i + 1) * 73) % 1000).padStart(3, '0')}`;

/**
 * Reusable grid of polaroid-mugshot candidate cards used by the vote
 * (online instant-vote) and eliminate (standalone two-step) screens.
 * Each card is a paper-sheet polaroid with the player's mugshot, file
 * tag, initial, and handwritten name. Picked state lifts the card +
 * adds a magenta tape stripe to the top.
 */
export const VoteCandidateGrid = ({
  candidates,
  pickedId,
  onPick,
  disabled,
}: VoteCandidateGridProps) => {
  const t = useT();
  return (
    <ul className="mx-auto grid w-full max-w-md grid-cols-2 gap-x-4 gap-y-6 sm:max-w-2xl sm:grid-cols-3 lg:grid-cols-4">
      {candidates.map((c, i) => {
        const tilt = TILTS[i % TILTS.length]!;
        const isPicked = pickedId === c.id;
        const initial = c.name.charAt(0).toUpperCase();
        return (
          <li key={c.id} className="flex justify-center">
            <motion.button
              type="button"
              disabled={disabled}
              initial={{ opacity: 0, y: 8, rotate: 0 }}
              animate={{ opacity: 1, y: isPicked ? -4 : 0, rotate: tilt }}
              whileTap={onPick && !disabled ? { y: 1, x: 1 } : undefined}
              transition={{ delay: i * 0.04, duration: 0.28 }}
              onClick={() => onPick?.(c.id)}
              aria-label={t('a11y.vote_board.candidate_aria_label', { name: c.name })}
              className="group relative flex w-full max-w-[160px] flex-col items-stretch"
            >
              <Pin left={'50%'} top={-6} size={12} />
              <PaperSheet
                tone="cream"
                seed={c.id}
                className="relative px-2 pt-2 pb-2.5"
                style={{
                  boxShadow: isPicked ? 'var(--shadow-stamp-md)' : 'var(--shadow-paper-soft)',
                }}
              >
                {isPicked && (
                  <span
                    aria-hidden
                    className="absolute top-0 right-0 left-0 h-1.5"
                    style={{ background: 'var(--color-magenta)' }}
                  />
                )}
                <div className="relative flex aspect-[4/5] w-full items-center justify-center overflow-hidden border border-ink/15">
                  <MugshotPortrait config={mugshotForPlayer(c)} />
                  <span className="absolute top-1 left-1.5 font-mono text-[8px] text-ink/55 tracking-[0.2em]">
                    #{String(c.order ?? i + 1).padStart(3, '0')}
                  </span>
                  <span className="absolute right-1.5 bottom-1 font-mono text-[8px] text-ink/55 tracking-[0.18em]">
                    {fileTag(i)}
                  </span>
                  <span className="absolute right-1.5 bottom-1 translate-y-[-14px] font-mono text-[10px] text-ink/55">
                    {initial}
                  </span>
                </div>
              </PaperSheet>
              <div className="mt-1 truncate text-center font-hand text-[20px] text-ink leading-tight">
                {c.name}
              </div>
            </motion.button>
          </li>
        );
      })}
    </ul>
  );
};
